import { Wifi, Zap, Armchair, Car } from "lucide-react";
import { GRADE_STYLES } from "@/components/cafe-detail/FeatureGradeCard";
import {
  getWifiGrade,
  getOutletsGrade,
  getSeatingGrade,
  getParkingGrade,
  type LetterGrade,
} from "@/lib/amenityGrades";
import { cn } from "@/lib/utils";

type FeatureKey = "wifi" | "outlets" | "seating" | "parking";

const FEATURE_ORDER: FeatureKey[] = ["wifi", "outlets", "seating", "parking"];

const FEATURE_META: Record<
  FeatureKey,
  { icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  wifi: { icon: Wifi, label: "WiFi" },
  outlets: { icon: Zap, label: "Outlets" },
  seating: { icon: Armchair, label: "Seating" },
  parking: { icon: Car, label: "Parking" },
};

interface AmenityLike {
  has_wifi?: boolean | null;
  wifi_type?: string | null;
  wifi_speed_avg?: number | null;
  outlet_availability?: string | null;
  seating_capacity?: string | null;
  parking_available?: string | null;
}

function gradeFor(feature: FeatureKey, amenities: AmenityLike | null | undefined): LetterGrade {
  switch (feature) {
    case "wifi":
      return getWifiGrade(amenities).grade;
    case "outlets":
      return getOutletsGrade(amenities).grade;
    case "seating":
      return getSeatingGrade(amenities).grade;
    case "parking":
      return getParkingGrade(amenities).grade;
  }
}

function ariaFor(label: string, grade: LetterGrade): string {
  if (grade === "U") return `${label} not verified yet`;
  return `${label} grade ${grade}`;
}

/** Chip fill per grade family. Local to the browse strip, not part of GRADE_STYLES. */
const CHIP_FILL: Record<LetterGrade, string> = {
  A: "#F1F5F0",
  B: "#FBF5E4",
  C: "#F8F1E9",
  D: "#F8F1E9",
  F: "#F7F3ED",
  U: "#FBF9F6",
};

const LABELED_CHIP_CLASS =
  "min-w-0 inline-flex items-center justify-between gap-2 rounded-full px-3.5 py-2";

const CHIP_CLASS =
  "flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5";

interface GradeStripProps {
  amenities?: AmenityLike | null;
  /** Letters already worked out (Your picks), used instead of amenities. */
  grades?: Partial<Record<FeatureKey, LetterGrade>> | null;
  /** Show each feature name beside its icon, letter on the right. */
  labeled?: boolean;
  className?: string;
}

/**
 * Four compact grade chips in a fixed order: WiFi, outlets, seating, parking.
 * Grades come from amenityGrades.ts, colors from the shared grade ring styles.
 */
export function GradeStrip({ amenities, grades, labeled = false, className }: GradeStripProps) {
  return (
    <div
      data-testid="grade-strip"
      className={cn("flex w-full items-stretch gap-1.5", className)}
    >
      {FEATURE_ORDER.map((feature) => {
        const { icon: Icon, label } = FEATURE_META[feature];
        const grade = grades ? grades[feature] ?? "U" : gradeFor(feature, amenities);
        const s = GRADE_STYLES[grade];
        return (
          <span
            key={feature}
            data-testid={`grade-chip-${feature}`}
            aria-label={ariaFor(label, grade)}
            className={labeled ? LABELED_CHIP_CLASS : CHIP_CLASS}
            style={{
              backgroundColor: CHIP_FILL[grade],
              borderColor: s.ringColor,
              borderWidth: 1.5,
              borderStyle: s.ringStyle ?? "solid",
              color: s.ink,
            }}
          >
            {labeled ? (
              <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={`grade-label-${feature}`}>
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="text-[13px] font-medium leading-none truncate">{label}</span>
              </span>
            ) : (
              <Icon className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="font-display text-[15px] font-semibold leading-none">
              {grade === "U" ? "\u2013" : grade}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Placeholder strip for signed out visitors. Contains no real grade data:
 * generic blurred chips plus a sign up hint.
 */
export function GuestGradeStrip({ className }: { className?: string }) {
  return (
    <div
      data-testid="grade-strip-guest"
      className={cn("relative flex w-full items-stretch", className)}
    >
      <div className="flex w-full items-stretch gap-1.5 blur-[3px] select-none" aria-hidden="true">
        {FEATURE_ORDER.map((feature) => {
          const { icon: Icon } = FEATURE_META[feature];
          return (
            <span
              key={feature}
              className={CHIP_CLASS}
              style={{
                backgroundColor: "#f5f1ec",
                borderColor: "#d9c9b0",
                borderWidth: 1.5,
                borderStyle: "solid",
                color: "#8b6f4e",
              }}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="font-display text-[15px] font-semibold leading-none">&nbsp;</span>
            </span>
          );
        })}
      </div>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-[#6B4F36]">
        Sign up to see work grades
      </span>
    </div>
  );
}

